import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { ArrowRight } from "lucide-react";
import { ABOUT, IMAGES } from "@/lib/constants";

function OfferingLink({ o }) {
  const cls =
    "inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#111111] hover:text-[#C9A227] transition-colors group/link";
  const inner = (
    <>
      <span>{o.cta}</span>
      <ArrowRight
        size={13}
        strokeWidth={1.5}
        className="transition-transform duration-500 group-hover/link:translate-x-1"
      />
    </>
  );
  return o.to.includes("#") ? (
    <HashLink smooth to={o.to} className={cls} data-testid={`who-offering-link-${o.n}`}>
      {inner}
    </HashLink>
  ) : (
    <Link to={o.to} className={cls} data-testid={`who-offering-link-${o.n}`}>
      {inner}
    </Link>
  );
}

export default function WhoWeAre() {
  return (
    <section
      id="hvem-er-vi"
      data-testid="who-we-are-section"
      className="bg-[#F1EFEA] py-16 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: intro + portrait */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <p className="overline mb-4 sm:mb-5 text-[10px] sm:text-xs">
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Hvem er vi
            </p>
            <h2
              className="font-serif text-[2.25rem] sm:text-5xl lg:text-[3.5rem] text-[#111111] font-light tracking-tight leading-[1.05]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Ét sted for <em className="italic text-[#C9A227]">ophold</em> og{" "}
              <em className="italic text-[#C9A227]">drift</em>.
            </h2>
            <p className="mt-6 text-[#444444] text-base sm:text-lg font-light leading-relaxed max-w-lg" data-testid="who-intro">
              {ABOUT.intro}
            </p>

            <div className="mt-8 flex items-center gap-5">
              <div className="relative h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded-full overflow-hidden bg-gradient-to-b from-[#1f1f1f] to-[#0a0a0a] border border-[#D4AF37]/40 flex items-end justify-center">
                <img
                  src={IMAGES.portrait}
                  alt="Gustav Gudiksen Nielsen"
                  className="h-[92%] w-auto object-contain object-bottom"
                  loading="lazy"
                />
              </div>
              <div>
                <div
                  className="font-serif text-lg sm:text-xl text-[#111111] leading-none"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Gustav Gudiksen <em className="italic">Nielsen</em>
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#C9A227] mt-1.5">
                  CEO & Founder · Sæby
                </div>
              </div>
            </div>

            <Link
              to="/om-os"
              data-testid="who-about-link"
              className="mt-10 btn-outline-dark group"
            >
              <span className="inline-flex items-center gap-2.5">
                Læs mere om os
                <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1" />
              </span>
            </Link>
          </motion.div>

          {/* Right: what we do */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {ABOUT.offerings.map((o, i) => (
              <motion.div
                key={o.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white p-7 sm:p-8 flex flex-col group"
                data-testid={`who-offering-${o.n}`}
              >
                <div
                  className="num-tag text-4xl sm:text-5xl"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {o.n}
                </div>
                <div className="h-px w-10 bg-[#D4AF37] my-5" />
                <h3
                  className="font-serif text-xl sm:text-2xl text-[#111111] font-light leading-tight"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {o.title}
                </h3>
                <p className="mt-3 text-sm text-[#555555] font-light leading-relaxed flex-1">
                  {o.desc}
                </p>
                <div className="mt-6">
                  <OfferingLink o={o} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
